import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useState } from "react";
import {
  Users, Leaf, SignOut, House, Image, Ruler, ShieldCheck,
  User as UserIcon, Buildings, List as HamburgerIcon, X as CloseIcon,
  Basket,
} from "@phosphor-icons/react";
import { Toaster } from "sonner";

function NavItem({ to, icon: Icon, label, testId, onClick }) {
  const loc = useLocation();
  const active = loc.pathname === to || (to !== "/" && loc.pathname.startsWith(to));
  return (
    <Link
      to={to}
      data-testid={testId}
      onClick={onClick}
      className="flex items-center gap-3 px-4 py-2.5 rounded-full text-sm transition-colors"
      style={{
        background: active ? "var(--text-primary)" : "transparent",
        color: active ? "var(--bg-primary)" : "var(--text-primary)",
      }}
    >
      <Icon size={18} weight={active ? "fill" : "duotone"} />
      <span>{label}</span>
    </Link>
  );
}

function RoleBadge({ isAdmin, size = "md" }) {
  const bg = isAdmin ? "#1A1A18" : "#63705B";
  const label = isAdmin ? "Area Amministratore" : "Area Cliente";
  const Icon = isAdmin ? ShieldCheck : UserIcon;
  const sizes = size === "sm"
    ? { pad: "px-2.5 py-1", text: "text-[10px]", icon: 12, gap: "gap-1.5" }
    : { pad: "px-3.5 py-1.5", text: "text-xs", icon: 14, gap: "gap-2" };
  return (
    <div
      data-testid={isAdmin ? "role-badge-admin" : "role-badge-client"}
      className={`inline-flex items-center ${sizes.gap} ${sizes.pad} rounded-full ${sizes.text} tracking-[0.15em] uppercase font-medium`}
      style={{ background: bg, color: "#FCFAF8" }}
    >
      <Icon size={sizes.icon} weight="fill" />
      <span>{label}</span>
    </div>
  );
}

function SidebarContent({ user, isAdmin, logout, onNavClick }) {
  return (
    <div className="h-full flex flex-col justify-between" style={{ background: "var(--bg-secondary)" }}>
      <div className="p-6 lg:p-8">
        <div className="flex items-center gap-2">
          <img src="/logo-mf.jpg" alt="Metodo Funzionale" className="w-9 h-9 rounded-full object-cover ring-1 ring-[color:var(--border)]" />
          <div className="font-serif text-2xl tracking-tight">Metodo Funzionale</div>
        </div>
        <div className="mt-3 ml-[34px]">
          <RoleBadge isAdmin={isAdmin} size="sm" />
        </div>

        <nav className="mt-10 space-y-1">
          {isAdmin ? (
            <>
              <NavItem to="/admin" icon={Users} label="Clienti" testId="nav-clients" onClick={onNavClick} />
              <NavItem to="/admin/studio" icon={Buildings} label="Studio" testId="nav-studio" onClick={onNavClick} />
            </>
          ) : (
            <>
              <NavItem to="/" icon={House} label="Home" testId="nav-home" onClick={onNavClick} />
              <NavItem to="/piani" icon={Leaf} label="Piani" testId="nav-plans" onClick={onNavClick} />
              <NavItem to="/porzionario" icon={Basket} label="Porzionario" testId="nav-porzionario" onClick={onNavClick} />
              <NavItem to="/rilievi" icon={Ruler} label="Rilievi" testId="nav-measurements" onClick={onNavClick} />
              <NavItem to="/foto" icon={Image} label="Foto" testId="nav-photos" onClick={onNavClick} />
            </>
          )}
        </nav>
      </div>

      <div className="p-6 lg:p-8 pt-6 border-t border-[color:var(--border)]">
        <div className="flex items-center gap-3 mb-3" data-testid="sidebar-user">
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
          ) : (
            <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium" style={{ background: "var(--bg-tertiary)" }}>
              {user?.name?.[0]?.toUpperCase() || "?"}
            </div>
          )}
          <div className="min-w-0">
            <div className="text-sm truncate">{user?.name}</div>
            <div className="label-tiny truncate">{user?.email}</div>
          </div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={logout}
          className="w-full flex items-center justify-center gap-2 text-sm py-2 rounded-full border border-[color:var(--border)] hover:border-[color:var(--text-primary)] transition-colors"
        >
          <SignOut size={16} weight="duotone" /> Esci
        </button>
      </div>
    </div>
  );
}

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const isAdmin = user?.role === "admin";
  const [mobileOpen, setMobileOpen] = useState(false);
  const loc = useLocation();

  // Close drawer on route change and lock body scroll while it's open.
  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [mobileOpen]);

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[260px_1fr]">
      {/* Desktop sidebar — hidden on mobile */}
      <aside className="hidden lg:block border-r border-[color:var(--border)]" style={{ background: "var(--bg-secondary)" }}>
        <SidebarContent user={user} isAdmin={isAdmin} logout={logout} />
      </aside>

      {/* Mobile drawer — slides in from the left */}
      {mobileOpen && (
        <>
          <div
            data-testid="mobile-drawer-backdrop"
            onClick={() => setMobileOpen(false)}
            className="fixed inset-0 z-40 lg:hidden"
            style={{ background: "rgba(26,26,24,0.45)" }}
          />
          <aside
            data-testid="mobile-drawer"
            className="fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] shadow-xl lg:hidden animate-in slide-in-from-left"
          >
            <button
              data-testid="close-mobile-drawer"
              onClick={() => setMobileOpen(false)}
              className="absolute top-3 right-3 z-10 w-9 h-9 inline-flex items-center justify-center rounded-full hover:bg-[color:var(--bg-tertiary)] transition-colors"
              aria-label="Chiudi menu"
            >
              <CloseIcon size={18} weight="bold" />
            </button>
            <SidebarContent
              user={user}
              isAdmin={isAdmin}
              logout={logout}
              onNavClick={() => setMobileOpen(false)}
            />
          </aside>
        </>
      )}

      <div className="flex flex-col min-w-0">
        {/* Top bar: always shows role + user info + hamburger on mobile */}
        <header
          data-testid="top-bar"
          className="sticky top-0 z-30 border-b border-[color:var(--border)] backdrop-blur-md"
          style={{ background: "rgba(252, 250, 248, 0.85)" }}
        >
          <div className="px-4 sm:px-6 lg:px-12 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              {/* Mobile hamburger */}
              <button
                data-testid="open-mobile-drawer"
                onClick={() => setMobileOpen(true)}
                className="lg:hidden w-9 h-9 inline-flex items-center justify-center rounded-full border border-[color:var(--border)] hover:bg-[color:var(--bg-secondary)] transition-colors flex-shrink-0"
                aria-label="Apri menu"
              >
                <HamburgerIcon size={18} weight="bold" />
              </button>
              <RoleBadge isAdmin={isAdmin} />
              <div className="hidden sm:block h-5 w-px" style={{ background: "var(--border)" }} />
              <div className="hidden sm:flex items-center gap-2 min-w-0">
                <span className="label-tiny">Accesso come</span>
                <span className="text-sm font-medium truncate" data-testid="topbar-user-name">
                  {user?.name}
                </span>
                <span className="text-xs text-[color:var(--text-secondary)] hidden md:inline truncate">
                  · {user?.email}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
              ) : (
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium" style={{ background: "var(--bg-tertiary)" }}>
                  {user?.name?.[0]?.toUpperCase() || "?"}
                </div>
              )}
              <button
                data-testid="topbar-logout-btn"
                onClick={logout}
                className="hidden sm:inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[color:var(--border)] hover:border-[color:var(--text-primary)] transition-colors"
              >
                <SignOut size={13} weight="duotone" /> Esci
              </button>
            </div>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-12 max-w-[1400px] w-full mx-auto fade-up flex-1">
          {children}
        </main>
      </div>
      <Toaster position="top-right" richColors />
    </div>
  );
}
